import { createStore, useStore } from "zustand";
import { immer } from "zustand/middleware/immer";
import { ImmerGet, ImmerSet } from "..";
import { createContext, useContext, useMemo } from "react";
import type { ElementDraftSource } from "@repo/video-engine-i2v/utils/element-draft";
import { CanvasState } from "./canvas-state";
import { CanvasService } from "./canvas-service";

export type Set = ImmerSet<CanvasState>;
export type Get = ImmerGet<CanvasState>;

export const createCanvasStore = () => {
  return createStore<CanvasState>()(
    immer<CanvasState>((set, get) => ({
      frame: 0,
      canvasElements: [],
      marquee: null,
      drafts: {},
      guidelines: [],
      activeHandle: null,
      hoveredId: null,
      editingTextId: null,
      service: new CanvasService(set, get),
    })),
  );
};

export const CanvasStoreContext = createContext<ReturnType<
  typeof createCanvasStore
> | null>(null);

function useCanvasStoreApi() {
  const store = useContext(CanvasStoreContext);

  if (store === null) {
    throw new Error("useCanvasStore must be used within CanvasStoreContext");
  }

  return store;
}

export function useCanvasStore<U>(selector: (state: CanvasState) => U) {
  return useStore(useCanvasStoreApi(), selector);
}

export function useElementDraftSource(): ElementDraftSource {
  const store = useCanvasStoreApi();

  return useMemo(
    () => ({
      subscribe: store.subscribe,
      getDraft: (externalId) => store.getState().drafts[externalId],
    }),
    [store],
  );
}
